import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bar } from "react-chartjs-2";
import "chart.js/auto";
import {
  STATUS,
  INK,
  MUTED,
  CONTEXT,
  OUT_OF_SESSION_COLOR,
  UTIL_STATES,
  STACK_ORDER,
  TRAY,
  nestBg,
} from "../tokens";
import { seasonShadeDetail } from "../schoolyear";
import { fmtMonth, fmtBucketLabel, fmtRecentSpan } from "../format";
import {
  chartOptions,
  categoryScales,
  axisTitle,
  MAX_BAR_THICKNESS,
} from "../charts";
import { Sparkline } from "../primitives";
import {
  IDLE_FLAG_LABEL,
  isExpired,
  isIdleFlagged,
  recentState,
} from "../fleet";
import {
  Card,
  ChartCard,
  TableCard,
  Table,
  HeadRow,
  Th,
  SortTh,
  Td,
  Caret,
  Row,
  GhostButton,
  SectionHead,
  Prose,
  Legend,
  PlotNote,
  RailStat,
  StatRail,
  TableNote,
  DEFAULT_ROWS,
} from "../ui";

// ---- Tab 4: Robots & investment ---------------------------------------------
// The state palette (UTIL_STATES / STACK_ORDER) lives in tokens.js and the
// robot-state model in ../fleet — this file draws them and owns neither. It used
// to own both and re-export them, which is how RenewalBrief ended up importing a
// palette from a tab.

const fmtDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "—";

// share of a robot's *in-session* post-deployment buckets that were active.
// `oos` is the per-bucket out-of-session mask: summer/break buckets are dropped
// from the denominator so a robot isn't penalized for being idle when school
// was out (the robot's state in those buckets is still whatever it really was).
const activeRate = (r, oos) => {
  let deployed = 0;
  let active = 0;
  r.states_by_bucket.forEach((s, i) => {
    if (s === "pre" || (oos && oos[i])) return;
    deployed += 1;
    if (s === "active") active += 1;
  });
  return deployed ? active / deployed : null;
};

// ---- sorting -----------------------------------------------------------------
// The foot table's default order is `byConcern` below -- dark first, longest
// idle first -- which is a priority list, not a sort. Clicking a header replaces
// it with an ordinary column sort, and there is a way back to priority order,
// because the default IS the answer to "what should I do first" and a reader who
// sorted by lessons to check something should not have to reload to get it back.
//
// Which way a column sorts on first click belongs with the column: names want
// A→Z, measures want biggest-first, and dates want most-recent-first. `status`
// ascending is dark → connected → active, so its first click is worst-first like
// every other column's.
const SORT_DIR = {
  robot: "asc",
  school: "asc",
  lessons: "desc",
  learners: "desc",
  last: "desc",
  idle: "desc",
  status: "asc",
  sub: "asc",
};
// `[label, ascending phrase, descending phrase]`. The phrase is per column
// because "lowest first" is meaningless for a date and wrong for a status: what
// the reader wants told back to them is the ORDER, in the words of the thing
// being ordered.
const SORT_WORDS = {
  robot: ["robot", "A→Z", "Z→A"],
  school: ["school", "A→Z", "Z→A"],
  lessons: ["lessons", "fewest first", "most first"],
  learners: ["learners", "fewest first", "most first"],
  last: ["last active", "longest ago first", "most recent first"],
  idle: ["idle periods", "fewest first", "longest first"],
  status: ["status", "dark first", "active first"],
  sub: ["subscription end", "soonest first", "latest first"],
};

// The sparkline column is deliberately absent: it is a shape, not a quantity, and
// the quantity behind it is already sortable as `lessons`.
const schoolsLabel = (r) =>
  r.orgunits && r.orgunits.length
    ? r.orgunits.map((o) => o.name).join(", ")
    : "Unassigned";

// ---- selection ---------------------------------------------------------------
// Clicking a band of the fleet plot, a school's mini-bar, or a renewal cohort
// answers "which robots are those?" by filtering the table at the foot of the
// tab. Nothing here fetches: every robot the tab draws is already on the client
// (the whole page is built from `util.robots`), so a selection is a filter over
// an array and lands in the same frame as the click.
//
// A selection is a set of optional FACETS, intersected -- "dark", "dark in
// March", "dark at Lincoln", "everything at Lincoln", "dark in March at
// Lincoln". Each source patches only the facets it owns, which is what lets a
// school row and a plot band combine instead of replacing each other. Clicking a
// value that is already selected drops those facets again, so every one of these
// is a toggle rather than a mode you have to know how to leave.
//
// `bucket` is only ever set alongside `state`, by the plot: it says WHEN the
// state is read, and on its own it would mean nothing (every robot exists in
// every bucket). The rollup tables patch `bucket: null` with their state because
// their mini-bars are drawn from `lastState` -- they are about now.
const selIsEmpty = (s) => !s || Object.keys(s).every((k) => s[k] == null);

// The state a robot is in at a bucket, or the state it is in now when the
// selection names no bucket.
//
// "Now" is the trailing four-week read, NOT the last bucket: that bucket is the
// current calendar month clipped to today, so on the 1st it is one day long and
// every robot in the district reads dark -- which used to empty this table's
// Status column and fill the worklist with the whole fleet. See fleet.recentState.
const stateAt = (r, bucket) =>
  bucket == null ? recentState(r) : r.states_by_bucket[bucket] || "dark";

// Same fallback the per-school rollup uses, so an unassigned robot is selectable
// by the "Unassigned" row rather than being unreachable.
const schoolIdsOf = (r) =>
  r.orgunits && r.orgunits.length ? r.orgunits.map((o) => o.id) : ["none"];

const cohortKeyOf = (r) => (r.sub_end_date ? r.sub_end_date.slice(0, 7) : "none");

// The bucket a row with no label falls into, at every level of the tree. One
// sentinel rather than null, because it has to survive being a selection facet
// value where null already means "this facet is not set".
const UNATTRIBUTED = "none";

// How many teacher rows one grade shows before deferring to the table below.
// The card is half-width, and a grade with thirty teachers under it stops being
// a comparison and becomes a scroll -- clicking the grade row lists all of its
// robots in the foot table, which is the better home for a long list anyway.
const TEACHER_ROWS = 8;

// Grade and teacher are MULTI-VALUED on a robot -- it served two grades, three
// teachers share it -- so membership is an includes(), not an equality, and a
// robot legitimately appears under several rows. Same rule the per-school rollup
// has always followed for a robot shared across schools.
const hasLabel = (list, want) =>
  want === UNATTRIBUTED ? !(list && list.length) : (list || []).includes(want);

const matchesSel = (r, sel) => {
  if (sel.state != null && stateAt(r, sel.bucket) !== sel.state) return false;
  if (sel.school != null && !schoolIdsOf(r).includes(sel.school)) return false;
  if (sel.cohort != null && cohortKeyOf(r) !== sel.cohort) return false;
  if (sel.grade != null && !hasLabel(r.grades, sel.grade)) return false;
  if (sel.teacher != null && !hasLabel(r.teacher_keys, sel.teacher)) return false;
  return true;
};

// ---- the By-school tree ------------------------------------------------------
// school -> the grades its robots served -> the teachers those robots belong to.
//
// Neither of the lower two levels is a field on a robot; both are built by the
// backend from the learners who used it and from its RobotTag assignment (see
// vanroboapp/robot_utilization/attribution.py). Two consequences the card has to
// carry rather than hide:
//
//   * rows OVERLAP and sum past the fleet total -- a robot serving two grades is
//     in both, and the per-school level already worked this way.
//   * a robot that taught nobody has no grade to derive, and one with neither a
//     tag nor usage has no teacher either. Those get their own row at each level
//     instead of being dropped, because on this tab they are the population that
//     matters most: they are exactly the dark ones.
const fleetRollup = (rs) => {
  const states = rs.map(recentState);
  return {
    n: rs.length,
    active: states.filter((s) => s === "active").length,
    idle: states.filter((s) => s === "connected").length,
    dark: states.filter((s) => s === "dark").length,
    lessons: rs.reduce((a, r) => a + r.lessons_total, 0),
  };
};

// Group by a key list that may be empty (-> UNATTRIBUTED) or hold several
// (-> the robot is credited to each).
const groupByLabels = (rs, labelsOf) => {
  const out = new Map();
  rs.forEach((r) => {
    const labels = labelsOf(r);
    (labels && labels.length ? labels : [UNATTRIBUTED]).forEach((k) => {
      if (!out.has(k)) out.set(k, []);
      out.get(k).push(r);
    });
  });
  return out;
};

const gradeRowLabel = (g) =>
  g === UNATTRIBUTED
    ? "Grade not known"
    : g === 0
    ? "Kindergarten"
    : g <= -1
    ? "PreK"
    : `Grade ${g}`;

// Known grades ascending, the unlabelled bucket last -- the ordering
// LearnerSegments._split_grade emits, so a grade list here and a grade breakdown
// elsewhere on the dashboard read the same way.
const gradeRowsOf = (rs) =>
  [...groupByLabels(rs, (r) => r.grades).entries()]
    .map(([value, robots]) => ({
      key: `g:${value}`,
      value,
      label: gradeRowLabel(value),
      robots,
      ...fleetRollup(robots),
    }))
    .sort((a, b) =>
      a.value === UNATTRIBUTED
        ? 1
        : b.value === UNATTRIBUTED
        ? -1
        : a.value - b.value
    );

// By surname, the unlabelled bucket last. The sort key travels on the payload
// because a display name is given-name-first and the order is surname-first, and
// splitting one out of the other is a guess that files "Mary Van Der Berg" under
// the wrong letter.
const teacherRowsOf = (rs, names) =>
  [...groupByLabels(rs, (r) => r.teacher_keys).entries()]
    .map(([value, robots]) => {
      const meta = value === UNATTRIBUTED ? null : names[value];
      return {
        key: `t:${value}`,
        value,
        label: meta ? meta.name : "No teacher on record",
        sort: meta ? meta.sort : "",
        robots,
        ...fleetRollup(robots),
      };
    })
    .sort((a, b) =>
      a.value === UNATTRIBUTED
        ? 1
        : b.value === UNATTRIBUTED
        ? -1
        : a.sort.localeCompare(b.sort)
    );


// Merge a facet patch, toggling. "Same" means every facet in the patch already
// holds that value -- so re-clicking the March dark band clears state AND
// bucket together, while clicking February's dark band moves the bucket instead
// of clearing anything.
const toggleSel = (cur, patch) => {
  const prev = cur || {};
  const same = Object.keys(patch).every((k) => prev[k] === patch[k]);
  const cleared = {};
  Object.keys(patch).forEach((k) => {
    cleared[k] = null;
  });
  const next = { ...prev, ...(same ? cleared : patch) };
  return selIsEmpty(next) ? null : next;
};

// Short state words for the selection chip. UTIL_STATES.label is written for a
// legend, where it has to carry its own definition ("Connected, no lessons");
// inside a sentence that already says "robots" the long forms read as captions.
const SEL_STATE_WORD = {
  active: "active",
  connected: "connected but idle",
  dark: "dark",
};

// An unstyled clickable inside a table cell. A <button> rather than a span with
// an onClick so the selection is reachable from the keyboard -- these cells are
// the only way to build a selection that the plot cannot express.
const PickButton = ({ onClick, title, children, style }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    style={{
      border: "none",
      background: "none",
      padding: 0,
      margin: 0,
      font: "inherit",
      color: "inherit",
      textAlign: "inherit",
      cursor: "pointer",
      display: "block",
      maxWidth: "100%",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      ...style,
    }}
  >
    {children}
  </button>
);

const UtilPill = ({ state }) => {
  if (state === "pre")
    return <span style={{ color: MUTED }}>Not yet deployed</span>;
  const s = UTIL_STATES[state];
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        fontWeight: 600,
        color: s.color,
      }}
    >
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          background: s.color,
          display: "inline-block",
        }}
      />
      {s.label}
    </span>
  );
};

// compact per-school fleet state: proportional colored segments + "x/y active"
// (identity also carried by the title text + count, never color alone)
//
// `onPick(state)` makes each segment a selector for that school's robots in that
// state; `picked` is the state currently selected here, drawn with a dark rule
// under it. Outline rather than dimming its neighbours, because the segment
// widths ARE the comparison -- fading two of the three to select the third would
// destroy the reading the bar exists to give.
const FleetMiniBar = ({ active, idle, dark, width = 64, onPick, picked }) => {
  const total = active + idle + dark;
  if (!total) return <span style={{ color: MUTED }}>—</span>;
  const seg = (n, color, key, state, label) => {
    if (n <= 0) return null;
    const style = {
      display: "inline-block",
      width: Math.max((width * n) / total, 3),
      height: 10,
      background: color,
      borderRadius: 2,
      // Same ink outline the picked band in the plot wears, so one mark means
      // "this is the selection" in both places.
      outline: picked === state ? `2px solid ${INK}` : undefined,
      outlineOffset: picked === state ? 1 : undefined,
    };
    if (!onPick) return <span key={key} style={style} />;
    return (
      <button
        key={key}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onPick(state);
        }}
        title={`${n} ${label} — click to list`}
        style={{
          border: "none",
          background: "none",
          padding: 0,
          margin: 0,
          lineHeight: 0,
          cursor: "pointer",
        }}
      >
        <span style={style} />
      </button>
    );
  };
  return (
    <span
      style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
      title={
        onPick ? undefined : `${active} active · ${idle} connected-idle · ${dark} dark`
      }
    >
      <span style={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
        {seg(active, UTIL_STATES.active.color, "a", "active", "active")}
        {seg(idle, UTIL_STATES.connected.color, "i", "connected", "connected but idle")}
        {seg(dark, UTIL_STATES.dark.color, "d", "dark", "dark")}
      </span>
      <span style={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
        {active}/{total}
      </span>
    </span>
  );
};

// subscription-expiration chip: red once expired, amber inside 60 days
const SubEndChip = ({ iso }) => {
  if (!iso) return <span style={{ color: MUTED }}>—</span>;
  const end = new Date(iso);
  const days = Math.round((end - new Date()) / 86400000);
  const color =
    days < 0
      ? STATUS.critical.color
      : days <= 60
      ? STATUS.warning.color
      : INK;
  return (
    <span style={{ color, fontWeight: days <= 60 ? 600 : 400 }}>
      {fmtDate(iso)}
      {days < 0 ? " (expired)" : days <= 60 ? ` (${days}d)` : ""}
    </span>
  );
};

// One row renderer for all three levels of the By-school tree. The levels are
// the same object -- a set of robots, its fleet composition, its lessons per
// robot -- read at three depths, so they get one component and stay in vertical
// register as you drill down. Depth is carried by indent and a tint, not by a
// separate table, so the bars keep a shared axis and a grade can be compared
// against the school row it sits under.
//
// Module scope, not a closure inside RobotsTab: a component declared inside a
// render has a new type identity every render, so React unmounts and remounts
// the whole subtree on each keystroke of state -- which drops the browser's
// focus from the very button that was just clicked. `sel` and `onPick` come in
// as props instead.
const TreeRow = ({
  depth,
  label,
  row,
  facets,
  open,
  onToggle,
  toggleTitle,
  sel,
  onPick,
}) => {
  // A row is "picked" when the selection matches its facets EXACTLY -- same
  // school, same grade, same teacher. Matching loosely would light the school
  // row whenever any grade inside it was picked, which says the wrong thing:
  // the table below is listing the grade, not the school.
  const picked =
    !!sel &&
    sel.school === facets.school &&
    (sel.grade ?? null) === facets.grade &&
    (sel.teacher ?? null) === facets.teacher;
  return (
    <Row style={depth ? { background: nestBg(depth) } : undefined}>
      <Td truncate style={{ paddingLeft: 8 + depth * 18 }}>
        <span style={{ display: "flex", alignItems: "center", gap: 2, minWidth: 0 }}>
          {/* The caret keeps its slot at every depth, so the labels line up down
              a column whether or not a level can open. */}
          <Caret
            open={open}
            onClick={onToggle || undefined}
            disabled={!onToggle}
            title={onToggle ? `Show the ${toggleTitle}` : undefined}
          />
          <PickButton
            onClick={() => onPick(facets)}
            title={`${row.n} robot${row.n === 1 ? "" : "s"} — ${label} — click to list`}
            style={{ fontWeight: picked ? 600 : 400 }}
          >
            {label}
          </PickButton>
        </span>
      </Td>
      <Td>
        <FleetMiniBar
          active={row.active}
          idle={row.idle}
          dark={row.dark}
          picked={picked ? sel.state : null}
          onPick={(state) => onPick({ ...facets, state, bucket: null })}
        />
      </Td>
      <Td align="right">{(row.lessons / row.n).toFixed(0)}</Td>
    </Row>
  );
};

// Sortable header for the foot table, so a column is declared as "sortable by
// this key" and the widths stay the single thing they were. Module scope for the
// same focus reason as TreeRow -- SortTh goes out of its way to suppress the
// focus ring on a mouse click, and a remount would undo that work by dropping
// keyboard focus entirely.
const SortCol = ({ k, sort, onSort, ...rest }) => (
  <SortTh sortKey={k} sort={sort} onSort={onSort} defaultDir={SORT_DIR[k]} {...rest} />
);

const RobotsTab = ({ util }) => {
  const [expanded, setExpanded] = useState(false);
  // Expired-subscription robots are hidden by default so the fleet stats
  // reflect the paid, in-service fleet rather than a graveyard of lapsed
  // hardware. The toggle brings them back for renewal/audit views.
  const [includeExpired, setIncludeExpired] = useState(false);
  // Which robots the reader has picked out of the plot or the rollups. null =
  // nothing picked, and the foot of the tab shows its default worklist.
  const [sel, setSel] = useState(null);
  // null = the priority order the worklist is built in; a {key, dir} replaces it
  // with a column sort. Same shape and same "null means the natural order"
  // convention the Adoption worklist uses.
  const [sort, setSort] = useState(null);
  // Which branch of the By-school tree is open. One path at a time -- {} closed,
  // {school} one school open, {school, grade} down to its teachers -- rather
  // than a set of independently open rows: this card sits in a half-width column
  // and three schools expanded at once is a wall of rows nobody is comparing.
  const [open, setOpen] = useState({});
  const resultsRef = useRef(null);

  // A selection made from the plot is two screens above the table that answers
  // it, so bring the answer into view -- but only when it isn't already there,
  // so re-picking from the rollup tables (which sit just above it) doesn't jerk
  // the page on every click.
  const revealResults = () => {
    const el = resultsRef.current;
    if (!el) return;
    const { top } = el.getBoundingClientRect();
    if (top < 0 || top > window.innerHeight - 140)
      el.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const pickSel = (patch) => {
    const next = toggleSel(sel, patch);
    setSel(next);
    setExpanded(false);
    if (next) requestAnimationFrame(revealResults);
  };
  const clearSel = () => {
    setSel(null);
    setExpanded(false);
  };

  // A selection names a bucket by INDEX, so it does not survive a change of
  // window -- "dark in bucket 7" silently becomes a different month. Drop it
  // when the buckets underneath it change rather than let the heading lie.
  const windowKey =
    util && util.buckets && util.buckets.length
      ? `${util.interval}|${util.buckets.length}|${util.buckets[0].start}`
      : "";
  useEffect(() => {
    setSel(null);
    setExpanded(false);
    setOpen({});
  }, [windowKey]);
  if (!util || !util.robots || !util.robots.length)
    return (
      <div style={{ color: "#52514e" }}>
        No robot utilization data for this account/window. The Robots tab needs
        the <code>/stats/usage/robots/windowed/</code> endpoint — confirm the
        backend has it and that this account can see an organization or its
        OrgUnits.
      </div>
    );

  const { buckets, interval } = util;
  const openSchool = open.school ?? null;
  const openGrade = open.grade ?? null;
  // Names for the teacher level, and whether that level can be answered for this
  // window at all. Both come off the payload: the backend gates the teacher
  // level on the same ledger rule the teacher breakdown obeys, because a past
  // year cannot be attributed to teachers and answering it with today's roster
  // would be the one thing that gate exists to prevent.
  const teacherNames = util.teacher_names || {};
  const teachersAvailable = !!util.teachers_available;
  // expiredCount is always the full-fleet figure (the headline that motivates
  // the toggle); `robots` is the working set the rest of the tab is built from.
  const allRobots = util.robots;
  const expiredCount = allRobots.filter(isExpired).length;
  const robots = includeExpired
    ? allRobots
    : allRobots.filter((r) => !isExpired(r));

  // Toggle control, reused by both the normal render and the all-expired guard.
  const expiredToggle = expiredCount > 0 && (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        flexWrap: "wrap",
        margin: "0 0 20px",
      }}
    >
      <label
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          fontSize: 13,
          fontWeight: 600,
          cursor: "pointer",
        }}
      >
        <input
          type="checkbox"
          checked={includeExpired}
          onChange={(e) => setIncludeExpired(e.target.checked)}
        />
        Include expired robots
      </label>
      <span style={{ color: MUTED, fontSize: 12 }}>
        {includeExpired
          ? `${expiredCount} robot${expiredCount === 1 ? "" : "s"} with a lapsed subscription are included in the figures below.`
          : `${expiredCount} robot${expiredCount === 1 ? "" : "s"} with a lapsed subscription ${expiredCount === 1 ? "is" : "are"} hidden from the figures below.`}
      </span>
    </div>
  );

  // Everything is expired and hidden -- render the toggle + a prompt rather
  // than a page of zeros.
  if (!robots.length)
    return (
      <>
        <h2 style={{ margin: "0 0 4px" }}>Robots &amp; investment</h2>
        <Prose style={{ margin: "0 0 20px", maxWidth: "76ch" }}>
          Every robot on this account has an expired subscription, so there is
          no in-service fleet to report on.
        </Prose>
        {expiredToggle}
      </>
    );

  const n = buckets.length;
  const labels = buckets.map((b) => fmtBucketLabel(b.start, interval));
  // out-of-session (summer/break) mask, per bucket, from the backend calendar.
  // Not a state — robots keep their real active/connected/dark state in these
  // buckets — but these months drop out of cross-year rate stats (active rate,
  // idle streak) so being offline over the summer doesn't count against a robot.
  const oos = buckets.map((b) => !!b.out_of_session);
  // Buckets the window cut short -- in practice the trailing one, since the
  // window ends today. Their states are real but cover days where a full bucket
  // covers a month, so they are drawn faded and called out in the tooltip rather
  // than read as a collapse. Distinct from `oos`: an in-session month can be
  // partial, and after the nominal-span fix (buckets.nominal_span) the two no
  // longer coincide at all.
  const partial = buckets.map((b) => !!b.partial);
  // Why the shading starts where it does, when the backend detected this
  // district's own start of usage rather than assuming a nominal August one.
  // Read off the response the strip is already drawn from, so there is nothing to
  // thread through the page.
  const shadeDetail = seasonShadeDetail(util.season);

  // fleet counts derived from the robot rows (also covers the merged
  // per-OU fallback, which has no server-side fleet rollup)
  const fleet = buckets.map((_, i) => {
    const c = { active: 0, connected: 0, dark: 0, pre: 0 };
    robots.forEach((r) => {
      c[r.states_by_bucket[i] || "dark"] += 1;
    });
    return c;
  });
  // The fleet AS OF NOW, off the trailing four-week read rather than the last
  // bucket, for the same reason as the states above: on the 1st of a month that
  // bucket is one day long, and it reported a working district as 0 active and
  // wholly dark. `recentSpan` names the span so the rail can label its own
  // numbers -- early in a school year it is shorter than four weeks.
  const recentStates = robots.map(recentState);
  const deployedNow = recentStates.filter((st) => st !== "pre").length;
  const activeNow = recentStates.filter((st) => st === "active").length;
  const darkNow = recentStates.filter((st) => st === "dark").length;
  const recentSpan = fmtRecentSpan(util.recent && util.recent.days);

  const lessonsTotal = robots.reduce((a, r) => a + r.lessons_total, 0);
  // `idle_streak` is a bucket count, so the flag is grain-aware: the claim is
  // three months of silence at either grain (fleet.isIdleFlagged).
  const idleFlagged = robots.filter(
    (r) => recentState(r) !== "pre" && isIdleFlagged(r, interval)
  );
  // usage concentration: share of all lessons delivered by the busiest 20%
  const sorted = [...robots].sort((a, b) => b.lessons_total - a.lessons_total);
  const topN = Math.max(1, Math.ceil(robots.length * 0.2));
  const topShare =
    lessonsTotal > 0
      ? (100 *
          sorted.slice(0, topN).reduce((a, r) => a + r.lessons_total, 0)) /
        lessonsTotal
      : 0;

  // Summary rail, deliberately UNTINTED. The stacked plot directly below owns
  // green / amber / gray for active / connected / dark, and status-tinting the
  // numbers above it collides with that encoding twice over: an amber "Idle 3+"
  // reads as the amber connected band (it isn't — idle robots are counted in
  // whatever band they're actually in), and a red "Dark right now" reads as a
  // fourth, more serious state than the gray band showing those very robots.
  // Concern is carried by the wording instead, which also stops a fleet with two
  // dark robots from looking like an outage.
  const flag = (text) => (
    <span style={{ color: INK, fontWeight: 600 }}>{text}</span>
  );
  const stats = [
    // With expired robots hidden, `robots.length` is the in-service fleet, not
    // the number owned -- label it honestly and show the owned total as sub.
    includeExpired
      ? { label: "Robots owned", value: robots.length }
      : {
          label: "Robots in service",
          value: robots.length,
          sub: `of ${allRobots.length} owned`,
        },
    {
      label: "Active recently",
      value: `${activeNow} of ${deployedNow}`,
      // phrased as the Overview's fleet slot, which reports the same reading of
      // the same number -- and reads the same span, which is what makes that
      // true rather than merely intended
      sub: `deployed robots delivered lessons in the ${recentSpan}`,
    },
    {
      label: `Idle ${IDLE_FLAG_LABEL}`,
      value: idleFlagged.length,
      sub: idleFlagged.length
        ? flag("candidates to redeploy")
        : "none parked this long",
    },
    {
      label: "Dark right now",
      value: darkNow,
      sub: darkNow
        ? flag(`no connection in the ${recentSpan}`)
        : "every robot is connecting",
    },
    {
      label: "Expired subscriptions",
      value: expiredCount,
      sub:
        expiredCount === 0
          ? "all subscriptions current"
          : includeExpired
          ? flag("included in the figures here")
          : flag("excluded from the figures here"),
    },
    { label: "Lessons this window", value: lessonsTotal.toLocaleString() },
  ];

  // stacked fleet bars — active anchored to the baseline, pre excluded so bar
  // height = deployed fleet (growth over the year is itself the signal)
  //
  // The picked band is outlined in ink rather than drawn by fading the rest:
  // these bars are a composition, and dimming two thirds of every stack to
  // single out the third destroys the very reading the chart is for. Note that
  // the bars are NOT redrawn to the selection -- they stay the whole district's
  // fleet, which is the context the picked band is being read against.
  // A selection with no bucket means "right now", which is the LAST bucket --
  // not every bucket. Lighting the whole row of dark bands for a mini-bar pick
  // would say the selection spans the year, and it doesn't.
  const isPicked = (key, i) =>
    !!sel &&
    sel.state === key &&
    (sel.bucket == null ? i === n - 1 : sel.bucket === i);
  const barData = {
    labels,
    datasets: STACK_ORDER.map((key) => ({
      label: UTIL_STATES[key].label,
      data: fleet.map((c) => c[key]),
      // Per-point, so a partial bucket's bands come out faded: at full strength
      // the newest column of a month one day old is a solid gray block that reads
      // as a fleet that died overnight.
      backgroundColor: labels.map(
        (_, i) =>
          UTIL_STATES[key].color +
          (partial[i] ? "59" : key === "active" ? "d9" : "b3")
      ),
      borderColor: sel ? labels.map((_, i) => (isPicked(key, i) ? INK : "#fff")) : "#fff",
      // 2px visual gap between stacked segments; the picked band trades it for a
      // rule of its own
      borderWidth: sel ? labels.map((_, i) => (isPicked(key, i) ? 2 : 1)) : 1,
      borderRadius: 3,
      maxBarThickness: MAX_BAR_THICKNESS,
    })),
  };
  // The tooltip reads the whole stack at once (mode "index"), which is right for
  // reading the fleet's composition but wrong for a click -- so both handlers
  // re-hit-test in "nearest"/intersect to find the ONE band under the cursor.
  // Without that, a click anywhere in the plot area would return all three
  // datasets and there would be no way to tell which band was meant.
  const bandAt = (evt, chart) => {
    const hits = chart.getElementsAtEventForMode(
      evt.native || evt,
      "nearest",
      { intersect: true },
      false
    );
    return hits.length ? hits[0] : null;
  };
  const barOptions = chartOptions({
    interaction: { mode: "index", intersect: false },
    onClick: (evt, _els, chart) => {
      const band = bandAt(evt, chart);
      if (!band) return;
      // Only the facets this source owns: a school picked below stays picked, so
      // the plot and the rollups compose instead of overwriting each other.
      pickSel({ state: STACK_ORDER[band.datasetIndex], bucket: band.index });
    },
    onHover: (evt, _els, chart) => {
      const canvas = evt.native && evt.native.target;
      if (canvas) canvas.style.cursor = bandAt(evt, chart) ? "pointer" : "default";
    },
    plugins: {
      tooltip: {
        callbacks: {
          footer: (items) => {
            const i = items.length ? items[0].dataIndex : 0;
            const c = fleet[i];
            const deployed = robots.length - c.pre;
            const base = `${deployed} deployed${c.pre ? ` · ${c.pre} not yet deployed` : ""}`;
            const lines = [base];
            if (oos[i])
              lines.push("Out of session — excluded from active rate & idle streak");
            if (partial[i])
              lines.push(`This ${interval} is still in progress — a partial period`);
            return lines.length > 1 ? lines : base;
          },
        },
      },
    },
    scales: categoryScales({
      x: { stacked: true },
      y: {
        stacked: true,
        title: axisTitle("robots"),
        ticks: { precision: 0 },
      },
    }),
  });

  // Shade out-of-session buckets behind the bars, so the excluded months are
  // visible at a glance (the states shown are real; these months just don't
  // feed the active-rate / idle-streak stats). Inline per-instance plugin --
  // no dependency; draws under the bars via beforeDraw.
  const anyOos = oos.some(Boolean);
  const oosShade = {
    id: "oosShade",
    beforeDraw(chart) {
      const { ctx, chartArea, scales } = chart;
      if (!chartArea || !scales.x) return;
      const xs = scales.x;
      const half = xs.width / oos.length / 2;
      ctx.save();
      ctx.fillStyle = OUT_OF_SESSION_COLOR;
      oos.forEach((isOos, i) => {
        if (!isOos) return;
        const c = xs.getPixelForValue(i);
        ctx.fillRect(c - half, chartArea.top, half * 2, chartArea.bottom - chartArea.top);
      });
      ctx.restore();
    },
  };

  // Needs-attention list: PROBLEM robots only (currently dark or
  // connected-idle), dark first, longest-idle first. Healthy/active robots
  // are deliberately excluded — the full fleet inventory already exists on
  // the Robots page; this list is the action items, not another fleet table.
  //
  // `pre` and `active` are in the rank only for the selected-robots mode below,
  // which can hold any state; the worklist never produces them.
  const stateRank = { dark: 0, connected: 1, active: 2, pre: 3 };
  const byConcern = (a, b) => {
    const ra = stateRank[recentState(a)];
    const rb = stateRank[recentState(b)];
    if (ra !== rb) return ra - rb;
    if (b.idle_streak !== a.idle_streak) return b.idle_streak - a.idle_streak;
    return a.lessons_total - b.lessons_total;
  };
  const worklist = robots
    .filter((r) => {
      const st = recentState(r);
      return st === "dark" || st === "connected";
    })
    .sort(byConcern);

  // The selection overrides the worklist in the same table. Two tables would be
  // the obvious alternative and it is the wrong one: they would carry identical
  // columns and the reader would have to work out which of them their click
  // landed in. Same table, two modes, and the heading says which.
  const selected = sel ? robots.filter((r) => matchesSel(r, sel)).sort(byConcern) : null;

  // Column sort, applied over whichever list is showing. Ties fall back to
  // `byConcern` so equal values (every robot with 0 lessons, say) still read
  // worst-first inside the tie rather than in whatever order the payload
  // happened to arrive in.
  //
  // A missing value sorts LAST in both directions. Sorting a null to the top of
  // "most recently active" would put robots that were never active at the head
  // of a list ordered by recency, which is the one place they cannot be read
  // correctly -- they belong to the idle question, and `idle` and `status`
  // answer that.
  const SORT_VALUE = {
    robot: (r) => (r.wifi || r.serial || "").toLowerCase(),
    school: (r) => schoolsLabel(r).toLowerCase(),
    lessons: (r) => r.lessons_total,
    learners: (r) => r.unique_learners,
    last: (r) => (r.last_active ? Date.parse(r.last_active) : null),
    idle: (r) => r.idle_streak,
    status: (r) => stateRank[recentState(r)],
    sub: (r) => (r.sub_end_date ? Date.parse(r.sub_end_date) : null),
  };
  const sortRows = (list) => {
    if (!sort || !SORT_VALUE[sort.key]) return list;
    const value = SORT_VALUE[sort.key];
    const mul = sort.dir === "asc" ? 1 : -1;
    return [...list].sort((a, b) => {
      const va = value(a);
      const vb = value(b);
      if (va == null && vb == null) return byConcern(a, b);
      if (va == null) return 1;
      if (vb == null) return -1;
      if (va < vb) return -mul;
      if (va > vb) return mul;
      return byConcern(a, b);
    });
  };
  const rows = sortRows(selected || worklist);
  const shown = expanded ? rows : rows.slice(0, DEFAULT_ROWS);

  // per-school rollup (a robot shared across OrgUnits counts in each)
  const bySchool = new Map();
  robots.forEach((r) => {
    const ous = r.orgunits && r.orgunits.length
      ? r.orgunits
      : [{ id: "none", name: "Unassigned" }];
    ous.forEach((ou) => {
      if (!bySchool.has(ou.id))
        bySchool.set(ou.id, { id: ou.id, name: ou.name, robots: [] });
      bySchool.get(ou.id).robots.push(r);
    });
  });
  const schoolRows = [...bySchool.values()]
    .map((g) => ({
      id: g.id,
      name: g.name,
      robots: g.robots,
      ...fleetRollup(g.robots),
    }))
    .sort((a, b) => a.lessons / a.n - b.lessons / b.n);

  // renewal cohorts: robots grouped by subscription-end month
  const nowMonth = new Date().toISOString().slice(0, 7);
  const cohortMap = new Map();
  robots.forEach((r) => {
    const key = r.sub_end_date ? r.sub_end_date.slice(0, 7) : "none";
    if (!cohortMap.has(key)) cohortMap.set(key, []);
    cohortMap.get(key).push(r);
  });
  const cohorts = [...cohortMap.entries()]
    .sort(([a], [b]) => (a === "none" ? 1 : b === "none" ? -1 : a < b ? -1 : 1))
    .map(([key, rs]) => {
      const rates = rs.map((r) => activeRate(r, oos)).filter((x) => x != null);
      return {
        key,
        // months strictly before the current one have fully lapsed
        expired: key !== "none" && key < nowMonth,
        label: key === "none" ? "No subscription on file" : fmtMonth(`${key}-01`),
        n: rs.length,
        avgRate: rates.length
          ? rates.reduce((a, b) => a + b, 0) / rates.length
          : null,
        lessons: rs.reduce((a, r) => a + r.lessons_total, 0),
        learners: rs.reduce((a, r) => a + r.unique_learners, 0),
      };
    });

  // Threaded into every TreeRow rather than closed over, because TreeRow lives
  // at module scope -- see the note there.
  const treeProps = { sel, onPick: pickSel };

  // Prose for the selection heading. Spelled out rather than left to the
  // highlighted marks: a reader who lands on this page from a shared link, or
  // who has scrolled the plot off the top, needs the heading alone to say what
  // the table beneath it is a list of.
  const selPhrases = [];
  if (sel) {
    if (sel.state)
      selPhrases.push(
        sel.bucket == null
          ? `${SEL_STATE_WORD[sel.state]} right now`
          : `${SEL_STATE_WORD[sel.state]} in ${labels[sel.bucket]}`
      );
    if (sel.school != null) {
      const g = bySchool.get(sel.school);
      selPhrases.push(g ? g.name : "Unassigned");
    }
    // Verbatim, not lower-cased: the phrases are joined by "·" as separate
    // labels, and "prek" and "kindergarten" read as typos in running text.
    if (sel.grade != null) selPhrases.push(gradeRowLabel(sel.grade));
    if (sel.teacher != null) {
      const t = teacherNames[sel.teacher];
      selPhrases.push(t ? t.name : "no teacher on record");
    }
    if (sel.cohort != null) {
      const c = cohorts.find((x) => x.key === sel.cohort);
      selPhrases.push(
        !c ? String(sel.cohort) : c.key === "none" ? c.label : `expiring ${c.label}`
      );
    }
  }
  // A state picked from the plot AND a school or cohort picked from a rollup is
  // a legitimate question ("dark in March, at Lincoln") but the plot band stays
  // district-wide, so the lit band counts more robots than the table lists. Say
  // so rather than leave the reader to find the discrepancy.
  const selNarrowed = !!(
    sel &&
    sel.state &&
    (sel.school != null || sel.cohort != null || sel.grade != null ||
      sel.teacher != null)
  );

  // "sortable by this key" and the widths stay the single thing they were.
  // Says what order the list is in whenever it is NOT the default priority
  // order, and offers the way back. Without it a reader who sorted by lessons
  // has no way to tell that "dark first" no longer holds, and no way to restore
  // it short of reloading the page.
  const sortNote = sort ? (
    <>
      sorted by {SORT_WORDS[sort.key][0]},{" "}
      {SORT_WORDS[sort.key][sort.dir === "asc" ? 1 : 2]} ·{" "}
      <button
        type="button"
        onClick={() => setSort(null)}
        style={{
          border: "none",
          background: "none",
          padding: 0,
          font: "inherit",
          color: "inherit",
          textDecoration: "underline",
          cursor: "pointer",
        }}
      >
        back to priority order
      </button>
    </>
  ) : null;

  return (
    <>
      <h2 style={{ margin: "0 0 12px" }}>Robots &amp; investment</h2>

      {expiredToggle}

      {/* One surface holding the fleet's headline numbers, matching the
          Overview's summary band — six bordered tiles inside a bordered panel
          was a box-in-a-box that cost a screenful of height before the plot. */}
      <Card pad="16px 20px 14px" style={{ marginBottom: 20 }}>
        <StatRail min={150}>
          {stats.map((s) => (
            <RailStat key={s.label} {...s} />
          ))}
        </StatRail>
      </Card>

      <Legend
        style={{ margin: "0 0 10px" }}
        items={[
          ...STACK_ORDER.map((key) => ({
            color: UTIL_STATES[key].color,
            label: UTIL_STATES[key].label,
          })),
          ...(anyOos
            ? [
                {
                  color: OUT_OF_SESSION_COLOR,
                  label: "Out of session (excluded from rates)",
                  muted: true,
                },
              ]
            : []),
        ]}
      />
      <ChartCard height={300} style={{ marginBottom: 0 }}>
        <Bar data={barData} options={barOptions} plugins={[oosShade]} />
      </ChartCard>
      <PlotNote
        details={
          <>
            <strong>Active</strong> robots delivered lessons.{" "}
            <strong>Connected</strong> robots phoned home but taught nothing — an
            adoption problem, fixed with training, not a hardware one.{" "}
            <strong>Dark</strong> robots aren't connecting at all — find them.
            Bar height is the deployed fleet, so its growth over the year is
            itself the signal; a robot's pre-deployment {interval}s are excluded
            throughout. Shaded <strong>out-of-session</strong> buckets (summer,
            school breaks
            {shadeDetail ? ", and the run-up to this district's own start" : ""})
            still show robots in their real state, but drop out of the
            active-rate and idle-streak figures so a fleet that's simply between
            school years isn't counted as neglected.
            {shadeDetail ? ` ${shadeDetail}` : ""} Historic connectivity is
            best-effort — a robot only keeps its latest sync timestamp — so past
            buckets may over-count dark.
          </>
        }
      >
        The fleet's state {interval} by {interval}. The busiest {topN} robot
        {topN > 1 ? "s" : ""} (top 20%) delivered {topShare.toFixed(0)}% of this
        window's lessons
        {topShare >= 60
          ? " — heavily concentrated; redeploy idle robots before buying more."
          : " — usage is reasonably spread."}{" "}
        <strong>Click a band</strong> to list those robots at the foot of the
        tab.
        {partial[n - 1] ? (
          <>
            {" "}
            The faded final bar is {interval === "week" ? "a week" : "a month"}{" "}
            still in progress: most of its robots read dark simply because it has
            barely happened, so read the rail above for where the fleet stands
            now.
          </>
        ) : null}
      </PlotNote>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))",
          gap: 16,
          marginTop: 24,
        }}
      >
        <Card>
          <h3 style={{ margin: "0 0 4px" }}>By school</h3>
          <p style={{ color: MUTED, fontSize: 12, margin: "0 0 8px" }}>
            Sorted least-used first — pair a school at the top with a saturated
            one at the bottom before buying more. Open a school for the grades
            its robots served, and a grade for the teachers those robots belong
            to — by assignment or because their students used them. Click any
            row, or one band of its fleet bar, to list those robots below.
            A robot counts under every school, grade and teacher it belongs to,
            so rows sum past the fleet total; one that taught nobody has no grade
            to derive and lands under <em>Grade not known</em>.
          </p>
          <Table fixed>
            <HeadRow tint={false}>
              <Th>School</Th>
              <Th width={168}>Fleet (active / idle / dark)</Th>
              <Th align="right" width={112}>Lessons / robot</Th>
            </HeadRow>
            <tbody>
              {schoolRows.map((s) => {
                const grades = openSchool === s.id ? gradeRowsOf(s.robots) : [];
                return (
                  <React.Fragment key={s.id}>
                    <TreeRow
                      {...treeProps}
                      depth={0}
                      label={s.name}
                      row={s}
                      facets={{ school: s.id, grade: null, teacher: null }}
                      open={openSchool === s.id}
                      onToggle={() =>
                        setOpen(openSchool === s.id ? {} : { school: s.id })
                      }
                      toggleTitle="grades this school's robots served"
                    />
                    {grades.map((g) => {
                      const gOpen = openGrade === g.key;
                      const teachers =
                        gOpen && teachersAvailable
                          ? teacherRowsOf(g.robots, teacherNames)
                          : [];
                      return (
                        <React.Fragment key={g.key}>
                          <TreeRow
                            {...treeProps}
                            depth={1}
                            label={g.label}
                            row={g}
                            facets={{
                              school: s.id,
                              grade: g.value,
                              teacher: null,
                            }}
                            open={gOpen}
                            // A past window has no teacher level to open into,
                            // so the caret is disabled rather than absent and
                            // the reason is spelled out once per school below.
                            onToggle={
                              teachersAvailable
                                ? () =>
                                    setOpen({
                                      school: s.id,
                                      grade: gOpen ? null : g.key,
                                    })
                                : null
                            }
                            toggleTitle="teachers these robots belong to"
                          />
                          {teachers.slice(0, TEACHER_ROWS).map((t) => (
                            <TreeRow
                              {...treeProps}
                              key={t.key}
                              depth={2}
                              label={t.label}
                              row={t}
                              facets={{
                                school: s.id,
                                grade: g.value,
                                teacher: t.value,
                              }}
                            />
                          ))}
                          {teachers.length > TEACHER_ROWS && (
                            <Row style={{ background: nestBg(2) }}>
                              <Td
                                colSpan={3}
                                style={{
                                  paddingLeft: 52,
                                  color: MUTED,
                                  fontSize: 12,
                                }}
                              >
                                and {teachers.length - TEACHER_ROWS} more — click{" "}
                                <strong>{g.label}</strong> above to list all{" "}
                                {g.n} of its robots.
                              </Td>
                            </Row>
                          )}
                        </React.Fragment>
                      );
                    })}
                    {openSchool === s.id && !teachersAvailable && (
                      <Row style={{ background: nestBg(1) }}>
                        <Td
                          colSpan={3}
                          style={{
                            paddingLeft: 34,
                            color: MUTED,
                            fontSize: 12,
                          }}
                        >
                          Which teacher a robot belonged to can't be
                          reconstructed for a past school year, so the grades
                          here don't open any further.
                        </Td>
                      </Row>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </Table>
        </Card>

        <Card>
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <h3 style={{ margin: "0 0 4px" }}>Renewal cohorts</h3>
            <Link
              to={{
                pathname: "/admin/insights/renewal-brief",
                search: window.location.search,
              }}
              style={{ fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" }}
            >
              Open renewal brief →
            </Link>
          </div>
          <p style={{ color: MUTED, fontSize: 12, margin: "0 0 8px" }}>
            Robots grouped by subscription end date, with each cohort's usage
            record this window — "active rate" is the share of deployed{" "}
            {interval}s a robot delivered lessons, averaged over the cohort.
            Click a cohort to list its robots below.
          </p>
          <Table>
            <HeadRow tint={false}>
              <Th>Expires</Th>
              <Th align="right">Robots</Th>
              <Th align="right">Active rate</Th>
              <Th align="right">Lessons</Th>
              <Th align="right">Learners</Th>
            </HeadRow>
            <tbody>
              {cohorts.map((c) => (
                <Row
                  key={c.key}
                  style={
                    sel && sel.cohort === c.key ? { background: TRAY } : undefined
                  }
                >
                  <Td
                    style={{
                      fontWeight: 600,
                      color: c.expired ? STATUS.critical.color : INK,
                    }}
                  >
                    <PickButton
                      onClick={() => pickSel({ cohort: c.key })}
                      title={`${c.n} robot${c.n === 1 ? "" : "s"} in this cohort — click to list`}
                      style={{ fontWeight: "inherit", color: "inherit" }}
                    >
                      {c.label}
                      {c.expired && (
                        <span style={{ fontWeight: 400 }}> (expired)</span>
                      )}
                    </PickButton>
                  </Td>
                  <Td align="right">{c.n}</Td>
                  <Td align="right">
                    {c.avgRate == null ? "—" : `${(100 * c.avgRate).toFixed(0)}%`}
                  </Td>
                  <Td align="right">{c.lessons}</Td>
                  <Td align="right">{c.learners}</Td>
                </Row>
              ))}
            </tbody>
          </Table>
        </Card>
      </div>

      <div ref={resultsRef} style={{ scrollMarginTop: 16 }}>
      <SectionHead
        style={{ margin: "24px 0 8px" }}
        title={selected ? "Selected robots" : "Robots needing attention"}
        sub={
          selected ? (
            <>
              {rows.length} of {robots.length} robots · {selPhrases.join(" · ")}
              {selNarrowed &&
                " — the lit band in the plot above still counts the whole district"}
              {sortNote && <> · {sortNote}</>}
            </>
          ) : (
            sortNote || undefined
          )
        }
        note={
          selected ? undefined : (
            <>
              {worklist.length} of {robots.length} robots idle or dark ·{" "}
              <Link to="/robots" style={{ fontWeight: 600 }}>
                full fleet on the Robots page →
              </Link>
            </>
          )
        }
        controls={
          selected ? (
            <GhostButton onClick={clearSel}>
              Clear selection
            </GhostButton>
          ) : undefined
        }
      />
      {/* Two empty states, because they mean opposite things: an empty worklist
          is good news, an empty selection is a question that came back with
          nothing and needs its own way out. */}
      {!selected && worklist.length === 0 && (
        <div style={{ color: UTIL_STATES.active.color, fontWeight: 600 }}>
          Every deployed robot delivered lessons this {interval} — nothing to
          chase.
        </div>
      )}
      {selected && rows.length === 0 && (
        <TableCard>
          <TableNote>
            No robot matches {selPhrases.join(" · ")}.
          </TableNote>
        </TableCard>
      )}
      {rows.length > 0 && (
      <TableCard>
        <Table fixed minWidth={980}>
          <HeadRow>
            <SortCol sort={sort} onSort={setSort} k="robot" width={118}>Robot</SortCol>
            <SortCol sort={sort} onSort={setSort} k="school">School</SortCol>
            <SortCol sort={sort} onSort={setSort} k="lessons" align="right" width={78}>Lessons</SortCol>
            <SortCol sort={sort} onSort={setSort} k="learners" align="right" width={82}>Learners</SortCol>
            {/* Not sortable: a sparkline is a shape, not a quantity, and the
                quantity behind it is the Lessons column next to it. */}
            <Th width={104}>Lessons / {interval}</Th>
            <SortCol sort={sort} onSort={setSort} k="last" width={102}>Last active</SortCol>
            <SortCol sort={sort} onSort={setSort} k="idle" align="right" width={88}>
              Idle {interval}s
            </SortCol>
            <SortCol sort={sort} onSort={setSort} k="status" width={168}>Status</SortCol>
            <SortCol sort={sort} onSort={setSort} k="sub" width={124}>Sub ends</SortCol>
          </HeadRow>
          <tbody>
            {shown.map((r) => {
              const st = recentState(r);
              const schools =
                r.orgunits && r.orgunits.length
                  ? r.orgunits.map((o) => o.name).join(", ")
                  : "Unassigned";
              return (
                <Row key={r.serial}>
                  <Td truncate title={`serial ${r.serial}`}>
                    <Link
                      to={`/robot/${r.serial}`}
                      style={{ fontWeight: 600, whiteSpace: "nowrap" }}
                    >
                      {r.wifi || r.serial}
                    </Link>
                  </Td>
                  <Td truncate title={schools}>
                    {schools}
                  </Td>
                  <Td align="right">{r.lessons_total}</Td>
                  <Td align="right">{r.unique_learners}</Td>
                  <Td>
                    <Sparkline
                      values={r.lessons_by_bucket}
                      color={st === "pre" ? CONTEXT : UTIL_STATES[st].color}
                    />
                  </Td>
                  <Td style={{ whiteSpace: "nowrap" }}>{fmtDate(r.last_active)}</Td>
                  <Td
                    align="right"
                    style={{
                      fontWeight: isIdleFlagged(r, interval) ? 600 : 400,
                      color: isIdleFlagged(r, interval)
                        ? STATUS.warning.color
                        : INK,
                    }}
                  >
                    {r.idle_streak}
                  </Td>
                  <Td style={{ whiteSpace: "nowrap" }}>
                    <UtilPill state={st} />
                  </Td>
                  <Td style={{ whiteSpace: "nowrap" }}>
                    <SubEndChip iso={r.sub_end_date} />
                  </Td>
                </Row>
              );
            })}
          </tbody>
        </Table>
      </TableCard>
      )}
      {rows.length > DEFAULT_ROWS && (
        <GhostButton
          onClick={() => setExpanded((x) => !x)}
          style={{ marginTop: 12 }}
        >
          {expanded ? "Show fewer" : `Show all ${rows.length} robots`}
        </GhostButton>
      )}
      </div>
    </>
  );
};


// Fleet-state helpers are exported for the district dashboard's Overview
// digest, which needs the same "what state is this robot in now / is it
// expired / is it parked" reads without rendering the tab.
export { RobotsTab };
